import { Roles } from 'src/common/types'
import type { RouteRecordRaw } from 'vue-router'
import { useClientContextStore } from 'src/client/stores'
import { Notify } from 'quasar'

const routeRecord: RouteRecordRaw[] = [
  {
    name: 'client.members',
    path: 'members',
    component: () => import('src/client/pages/member/ClientMembers.vue'),
    redirect: { name: 'client.members.list' },
    meta: {
      roles: [Roles.Client],
      supportedPlatforms: ['desktop', 'mobile', 'native'],
      excludeEnv: [],
    },
    children: [
      {
        name: 'client.members.list',
        path: '',
        component: () => import('src/client/pages/member/MemberList.vue'),
        meta: {
          roles: [Roles.Client],
          supportedPlatforms: ['desktop', 'mobile', 'native'],
          excludeEnv: [],
        },
      },
      {
        name: 'client.members.detail',
        path: ':id',
        component: () => import('src/client/pages/member/MemberDetail.vue'),
        meta: {
          roles: [Roles.Client],
          supportedPlatforms: ['desktop', 'mobile', 'native'],
          excludeEnv: [],
        },
      },
      {
        name: 'client.members.edit',
        path: ':id/edit',
        component: () => import('src/client/pages/member/MemberEdit.vue'),
        beforeEnter: async (to, from, next) => {
          const contextStore = useClientContextStore()

          const maxAttempts = 50
          const pollInterval = 100

          for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (contextStore.isContextReady) break
            await new Promise((resolve) => setTimeout(resolve, pollInterval))
          }

          if (!contextStore.isContextReady || !contextStore.loggedInMember) {
            Notify.create({
              type: 'negative',
              message: 'Please log in to edit members',
              position: 'top',
            })
            next({ name: 'client.members.list' })
            return
          }

          const memberId = to.params.id as string
          const loggedInMember = contextStore.loggedInMember

          // Find the member
          const member = contextStore.family?.members?.find(
            (m) => m.id === memberId,
          )

          if (!member) {
            next({ name: 'client.members.list' })
            return
          }

          // Check edit permissions based on the new rules:
          // 1. Own profile - always editable (including pending changes)
          // 2. Other members (portal or non-portal) - only primary can edit
          // 3. Non-primary members: read-only for all others

          const isEditingSelf = loggedInMember.id === member.id
          const isPrimary = loggedInMember.isPrimaryMember ?? false

          let canEdit = false

          if (isEditingSelf) {
            // Can always edit own profile (including pending changes)
            canEdit = true
          } else if (isPrimary) {
            // Primary members can edit all non-portal members
            const isPortalMember = member.isPortalMember ?? false
            if (!isPortalMember) {
              canEdit = true
            }
          }
          // Non-primary members: cannot edit anyone except themselves

          if (!canEdit) {
            Notify.create({
              type: 'negative',
              message: 'You do not have permission to edit this member',
              position: 'top',
            })
            next({
              name: 'client.members.detail',
              params: { id: memberId },
            })
            return
          }

          next()
        },
        meta: {
          roles: [Roles.Client],
          supportedPlatforms: ['desktop', 'mobile', 'native'],
          excludeEnv: [],
        },
      },
      {
        name: 'client.members.new',
        path: 'new',
        component: () => import('src/client/pages/member/MemberNew.vue'),
        meta: {
          roles: [Roles.Client],
          supportedPlatforms: ['desktop', 'mobile', 'native'],
          excludeEnv: [],
        },
      },
    ],
  },
]

export default routeRecord
